<template>
  <div v-if="debugStore.isEnabled" class="debug-wrapper">
    <!-- Backdrop -->
    <transition name="debug-fade">
      <div v-if="debugStore.isPanelOpen" class="debug-backdrop" @click="debugStore.toggle()" />
    </transition>

    <!-- Drawer -->
    <transition name="debug-slide">
      <div v-if="debugStore.isPanelOpen" class="debug-drawer">
        <!-- Header -->
        <div class="debug-header">
          <div class="d-flex align-center" style="gap: 11px; min-width: 0">
            <span class="debug-logo">
              <v-icon size="16" color="#FFC627">mdi-bug</v-icon>
            </span>
            <span class="debug-header-title">Debug Console</span>
            <span class="debug-env-chip" :style="{ color: debugStore.envColor, borderColor: debugStore.envColor }">
              <span class="debug-env-dot" :style="{ backgroundColor: debugStore.envColor }" />
              {{ debugStore.environment }}
            </span>
          </div>
          <button class="debug-close-btn" title="Close (Ctrl+Shift+D toggles panel)" @click="debugStore.toggle()">
            <v-icon size="16" color="currentColor">mdi-close</v-icon>
          </button>
        </div>

        <!-- Tabs -->
        <div class="debug-tabs">
          <button
            class="debug-tab"
            :class="{ 'debug-tab--active': activeTab === 'courses' }"
            @click="activeTab = 'courses'"
          >
            <v-icon size="14" class="me-1">mdi-card-text-outline</v-icon>
            Courses
          </button>
          <button
            class="debug-tab"
            :class="{ 'debug-tab--active': activeTab === 'confetti' }"
            @click="activeTab = 'confetti'"
          >
            <v-icon size="14" class="me-1">mdi-party-popper</v-icon>
            Confetti
          </button>
        </div>

        <!-- Body -->
        <div v-show="activeTab === 'courses'" class="debug-body">
          <!-- Section header -->
          <div class="debug-section-header">
            <span class="debug-section-title">V2 Course Cards</span>
            <button class="debug-action-btn" :disabled="debugStore.isLoading" @click="fetchEnrichedCourses">
              <v-progress-circular
                v-if="debugStore.isLoading"
                indeterminate
                size="13"
                width="2"
                class="me-1"
                color="#FFC627"
              />
              <v-icon v-else size="14" class="me-1">mdi-refresh</v-icon>
              {{ debugStore.isLoading ? 'Loading' : 'Fetch' }}
            </button>
          </div>

          <!-- Loading -->
          <div v-if="debugStore.isLoading" class="debug-empty">
            <v-progress-circular indeterminate color="#FFC627" size="22" width="2" />
            <span class="mt-3">Fetching enriched courses…</span>
          </div>

          <!-- Course list -->
          <div v-else-if="debugStore.enrichedCourses.length > 0" class="debug-course-list">
            <template v-for="(course, idx) in debugStore.enrichedCourses" :key="'dv2-' + idx">
              <div v-if="showSectionLabel(idx)" class="debug-section-label">
                {{ course.section?.name || 'Unknown' }}
              </div>
              <CourseCardCompact
                :course="course"
                :isLastCourse="idx === debugStore.enrichedCourses.length - 1"
                :showDetailsButton="false"
                :variant="getCourseVariant(course)"
                @button-action="() => {}"
                @reload="fetchEnrichedCourses"
                @reloadDashboardOrPathway="() => {}"
              />
            </template>
          </div>

          <!-- Error state -->
          <div v-else-if="debugStore.error" class="debug-error">
            <v-icon size="16" color="#F98A8A" class="me-1">mdi-alert-circle-outline</v-icon>
            <span>{{ debugStore.error }}</span>
          </div>

          <!-- Empty state -->
          <div v-else class="debug-empty">
            <span class="debug-empty-icon">
              <v-icon size="26" color="currentColor">mdi-card-text-outline</v-icon>
            </span>
            <span class="mt-3">No courses loaded</span>
            <span class="debug-empty-hint">Hit “Fetch” to pull enriched V2 cards.</span>
          </div>

          <!-- Divider -->
          <hr class="debug-divider" />

          <!-- Certificate registration section -->
          <div class="debug-section-header">
            <span class="debug-section-title">Register for Certificate</span>
          </div>

          <div class="d-flex align-center" style="gap: 8px">
            <v-text-field
              v-model="debugStore.certificateSlug"
              class="debug-input"
              density="compact"
              variant="outlined"
              placeholder="e.g. aws-cloud-computing"
              hide-details
              @keyup.enter="debugStore.certificateSlug?.trim() && !debugStore.isRegistering && registerForCertificate()"
            />
            <button
              class="debug-action-btn"
              :disabled="!debugStore.certificateSlug?.trim() || debugStore.isRegistering"
              @click="registerForCertificate"
            >
              <v-progress-circular
                v-if="debugStore.isRegistering"
                indeterminate
                size="13"
                width="2"
                class="me-1"
                color="#FFC627"
              />
              <v-icon v-else size="14" class="me-1">mdi-certificate-outline</v-icon>
              {{ debugStore.isRegistering ? 'Registering' : 'Register' }}
            </button>
          </div>

          <!-- Certificate feedback -->
          <div
            v-if="debugStore.certificateMessage"
            class="mt-3"
            :class="debugStore.certificateMessage.type === 'success' ? 'debug-success' : 'debug-error'"
          >
            <v-icon
              size="16"
              :color="debugStore.certificateMessage.type === 'success' ? '#6EE7A0' : '#F98A8A'"
              class="me-1"
            >
              {{
                debugStore.certificateMessage.type === 'success'
                  ? 'mdi-check-circle-outline'
                  : 'mdi-alert-circle-outline'
              }}
            </v-icon>
            <span>{{ debugStore.certificateMessage.text }}</span>
          </div>
        </div>

        <!-- Confetti tab -->
        <div v-show="activeTab === 'confetti'" class="debug-body">
          <div class="debug-section-header">
            <span class="debug-section-title">Celebration Confetti</span>
            <button class="debug-action-btn" @click="fireTestConfetti">
              <v-icon size="14" class="me-1">mdi-party-popper</v-icon>
              Fire
            </button>
          </div>
          <div class="debug-empty">
            <span class="debug-empty-icon">
              <v-icon size="26" color="currentColor">mdi-party-popper</v-icon>
            </span>
            <span class="mt-3">Test the celebration overlay</span>
            <span class="debug-empty-hint">
              Fires the reusable CelebrationOverlay with a sample certificate modal.
            </span>
          </div>
        </div>

        <!-- Footer -->
        <div class="debug-footer">
          <v-icon size="12" class="me-1">mdi-keyboard-outline</v-icon>
          <span><kbd>Ctrl</kbd> + <kbd>Shift</kbd> + <kbd>D</kbd> to toggle</span>
        </div>
      </div>
    </transition>

    <!-- Test celebration modal + confetti (debug only) -->
    <PokCertificateDialog
      :visible="showTestConfetti"
      :dialog-data="testConfettiData"
      @close="showTestConfetti = false"
    />

    <!-- Floating trigger -->
    <button
      class="debug-trigger"
      :class="{ 'debug-trigger--open': debugStore.isPanelOpen }"
      :title="debugStore.isPanelOpen ? 'Close debug console' : 'Open debug console'"
      @click="debugStore.toggle()"
    >
      <v-icon size="19" class="debug-trigger-icon" :color="debugStore.isPanelOpen ? '#8C1D40' : '#FFC627'">{{
        debugStore.isPanelOpen ? 'mdi-close' : 'mdi-bug'
      }}</v-icon>
    </button>
  </div>
</template>

<script>
import { defineComponent, onMounted, onUnmounted, ref } from 'vue';
import { useDebugStore } from '@/stores/debug';
import { useDebugPanel } from '@/composables/useDebugPanel';
import CourseCardCompact from '@/components/courses/CourseCardCompact.vue';
import PokCertificateDialog from '@/components/certificates/PokCertificateDialog.vue';

export default defineComponent({
  name: 'DebugPanel',

  components: {
    CourseCardCompact,
    PokCertificateDialog
  },

  setup() {
    const debugStore = useDebugStore();
    const { fetchEnrichedCourses, registerForCertificate } = useDebugPanel();

    const activeTab = ref('courses');

    // Sample data to exercise the reusable CelebrationOverlay + confetti.
    const showTestConfetti = ref(false);
    const testConfettiData = {
      connection_id: 'debug-test',
      title: 'STEM Foundations',
      credential_url: 'https://ea.asu.edu'
    };

    function fireTestConfetti() {
      // Collapse the debug drawer so the full-screen celebration isn't hidden behind it.
      if (debugStore.isPanelOpen) debugStore.toggle();
      // Re-trigger cleanly if it's already showing.
      showTestConfetti.value = false;
      requestAnimationFrame(() => {
        showTestConfetti.value = true;
      });
    }

    function initDebugMode() {
      const env = import.meta.env.VITE_ENV_HOOK || 'prod';
      if (env !== 'prod') {
        debugStore.enable();
      } else if (localStorage.getItem('showDebugPanel') === 'true') {
        debugStore.enable();
      }
    }

    function handleKeydown(e) {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        if (debugStore.isEnabled) {
          debugStore.disable();
        } else {
          debugStore.enable();
        }
      }
    }

    onMounted(() => {
      initDebugMode();
      window.addEventListener('keydown', handleKeydown);
    });

    onUnmounted(() => {
      window.removeEventListener('keydown', handleKeydown);
    });

    function showSectionLabel(idx) {
      const courses = debugStore.enrichedCourses;
      const currentSection = courses[idx]?.section?.name;
      if (!currentSection) return false;
      if (idx === 0) return true;
      return courses[idx - 1]?.section?.name !== currentSection;
    }

    function getCourseVariant(course) {
      if (course.section?.name === 'Transcripted Courses') return 'history';
      if (course.section?.name === 'Past Courses') return 'past';
      return 'schedule';
    }

    return {
      debugStore,
      fetchEnrichedCourses,
      registerForCertificate,
      showSectionLabel,
      getCourseVariant,
      activeTab,
      showTestConfetti,
      testConfettiData,
      fireTestConfetti
    };
  }
});
</script>

<style scoped>
/* Design tokens (scoped to the debug console) */
.debug-wrapper {
  --dbg-surface: #1b1b22;
  --dbg-surface-2: #22232c;
  --dbg-border: rgba(255, 255, 255, 0.09);
  --dbg-border-strong: rgba(255, 255, 255, 0.16);
  --dbg-text: #ececf1;
  --dbg-text-muted: #9a9aa8;
  --dbg-text-dim: #6d6d7a;
  --dbg-maroon: #8c1d40;
  --dbg-gold: #ffc627;
  font-family:
    'Inter',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    sans-serif;
}

/* Trigger button */
.debug-trigger {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1501;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  border: 1px solid rgba(255, 198, 39, 0.35);
  background: radial-gradient(circle at 30% 25%, #a52550 0%, #8c1d40 55%, #6f1533 100%);
  box-shadow:
    0 6px 18px rgba(140, 29, 64, 0.45),
    inset 0 1px 1px rgba(255, 255, 255, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease;
}

/* Keep the glyph above the gradient background and the pulse ring */
.debug-trigger-icon {
  position: relative;
  z-index: 1;
}

/* Soft pulsing ring to draw the eye without being noisy */
.debug-trigger::after {
  content: '';
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  border: 1px solid rgba(255, 198, 39, 0.5);
  opacity: 0;
  pointer-events: none;
  z-index: 0;
  animation: debug-pulse 2.6s ease-out infinite;
}

@keyframes debug-pulse {
  0% {
    transform: scale(0.85);
    opacity: 0.55;
  }
  70% {
    transform: scale(1.35);
    opacity: 0;
  }
  100% {
    opacity: 0;
  }
}

.debug-trigger:hover {
  transform: scale(1.08) rotate(-4deg);
  box-shadow:
    0 8px 24px rgba(140, 29, 64, 0.55),
    inset 0 1px 1px rgba(255, 255, 255, 0.2);
}

.debug-trigger--open {
  background: radial-gradient(circle at 30% 25%, #ffd35a 0%, #ffc627 55%, #e8ad00 100%);
  border-color: rgba(140, 29, 64, 0.35);
  box-shadow:
    0 6px 18px rgba(255, 198, 39, 0.4),
    inset 0 1px 1px rgba(255, 255, 255, 0.4);
}

.debug-trigger--open::after {
  animation: none;
}

.debug-trigger--open:hover {
  transform: scale(1.08) rotate(4deg);
  box-shadow: 0 8px 24px rgba(255, 198, 39, 0.5);
}

/* Backdrop */
.debug-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(10, 10, 14, 0.28);
  backdrop-filter: blur(1.5px);
  z-index: 1499;
}

/* Drawer */
.debug-drawer {
  position: fixed;
  bottom: 78px;
  right: 20px;
  width: 520px;
  max-height: 62vh;
  z-index: 1500;
  background: var(--dbg-surface);
  border-radius: 14px;
  border: 1px solid var(--dbg-border);
  box-shadow:
    0 24px 60px rgba(0, 0, 0, 0.5),
    0 2px 8px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: var(--dbg-text);
}

/* Header */
.debug-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid var(--dbg-border);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.04) 0%, rgba(255, 255, 255, 0) 100%);
}

.debug-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: linear-gradient(145deg, rgba(140, 29, 64, 0.9), rgba(140, 29, 64, 0.55));
  border: 1px solid rgba(255, 198, 39, 0.3);
  box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.12);
  flex-shrink: 0;
}

.debug-header-title {
  font-weight: 700;
  font-size: 13px;
  color: var(--dbg-text);
  letter-spacing: 0.2px;
  white-space: nowrap;
}

.debug-env-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px 2px 6px;
  border-radius: 999px;
  border: 1px solid;
  background: rgba(255, 255, 255, 0.04);
  text-transform: uppercase;
  letter-spacing: 0.6px;
  white-space: nowrap;
}

.debug-env-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  box-shadow: 0 0 6px currentColor;
}

.debug-close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  color: var(--dbg-text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    background 0.15s ease,
    color 0.15s ease;
}

.debug-close-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: var(--dbg-text);
}

/* Tabs */
.debug-tabs {
  display: flex;
  gap: 4px;
  padding: 8px 10px;
  border-bottom: 1px solid var(--dbg-border);
  background: rgba(0, 0, 0, 0.15);
}

.debug-tab {
  display: inline-flex;
  align-items: center;
  border: none;
  background: none;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  color: var(--dbg-text-muted);
  transition:
    color 0.15s ease,
    background 0.15s ease;
}

.debug-tab:hover {
  color: var(--dbg-text);
  background: rgba(255, 255, 255, 0.05);
}

.debug-tab--active {
  color: var(--dbg-gold);
  background: rgba(255, 198, 39, 0.12);
  box-shadow: inset 0 0 0 1px rgba(255, 198, 39, 0.25);
}

/* Body */
.debug-body {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
}

.debug-body::-webkit-scrollbar {
  width: 8px;
}

.debug-body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.12);
  border-radius: 4px;
  border: 2px solid transparent;
  background-clip: padding-box;
}

.debug-body::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.2);
  background-clip: padding-box;
}

.debug-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.debug-section-title {
  font-size: 10.5px;
  font-weight: 700;
  color: var(--dbg-text-muted);
  text-transform: uppercase;
  letter-spacing: 1px;
}

.debug-action-btn {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  color: var(--dbg-gold);
  background: rgba(255, 198, 39, 0.1);
  border: 1px solid rgba(255, 198, 39, 0.35);
  border-radius: 7px;
  padding: 5px 12px;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    transform 0.1s ease;
}

.debug-action-btn:hover:not(:disabled) {
  background: rgba(255, 198, 39, 0.2);
  border-color: rgba(255, 198, 39, 0.6);
}

.debug-action-btn:active:not(:disabled) {
  transform: translateY(1px);
}

.debug-action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Section labels */
.debug-section-label {
  display: inline-block;
  font-size: 10.5px;
  font-weight: 700;
  color: var(--dbg-gold);
  text-transform: uppercase;
  letter-spacing: 0.6px;
  padding: 4px 10px;
  margin: 12px 0 8px;
  background: rgba(255, 198, 39, 0.1);
  border-left: 3px solid var(--dbg-gold);
  border-radius: 0 6px 6px 0;
}

.debug-section-label:first-child {
  margin-top: 0;
}

/* Divider */
.debug-divider {
  border: none;
  border-top: 1px solid var(--dbg-border);
  margin: 18px 0;
}

/* Certificate input (Vuetify field overrides for dark surface) */
.debug-input {
  flex: 1;
}

.debug-input :deep(.v-field) {
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  font-size: 13px;
}

.debug-input :deep(.v-field__input) {
  color: var(--dbg-text);
  min-height: 36px;
  padding-top: 4px;
  padding-bottom: 4px;
}

.debug-input :deep(.v-field__input)::placeholder {
  color: var(--dbg-text-dim);
  opacity: 1;
}

.debug-input :deep(.v-field__outline) {
  --v-field-border-opacity: 0.16;
  color: #fff;
}

.debug-input :deep(.v-field--focused .v-field__outline) {
  --v-field-border-opacity: 1;
  color: var(--dbg-gold);
}

/* Success / error states */
.debug-success,
.debug-error {
  display: flex;
  align-items: flex-start;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-family: 'SFMono-Regular', ui-monospace, Menlo, monospace;
  word-break: break-word;
  line-height: 1.5;
}

.debug-success {
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(110, 231, 160, 0.3);
  color: #8ff0b4;
}

.debug-error {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(249, 138, 138, 0.3);
  color: #f8a5a5;
}

/* Empty state */
.debug-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 34px 12px;
  color: var(--dbg-text-muted);
  font-size: 13px;
  font-weight: 500;
}

.debug-empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--dbg-border);
  color: var(--dbg-text-dim);
}

.debug-empty-hint {
  margin-top: 4px;
  max-width: 280px;
  font-size: 12px;
  font-weight: 400;
  color: var(--dbg-text-dim);
  line-height: 1.5;
}

/* Course list */
.debug-course-list {
  display: flex;
  flex-direction: column;
}

/* Footer */
.debug-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 14px;
  border-top: 1px solid var(--dbg-border);
  background: rgba(0, 0, 0, 0.2);
  color: var(--dbg-text-dim);
  font-size: 10.5px;
}

.debug-footer kbd {
  display: inline-block;
  padding: 1px 5px;
  margin: 0 1px;
  font-family: 'SFMono-Regular', ui-monospace, Menlo, monospace;
  font-size: 10px;
  color: var(--dbg-text-muted);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid var(--dbg-border-strong);
  border-radius: 4px;
}

/* Transitions */
.debug-fade-enter-active,
.debug-fade-leave-active {
  transition: opacity 0.2s ease;
}

.debug-fade-enter-from,
.debug-fade-leave-to {
  opacity: 0;
}

.debug-slide-enter-active,
.debug-slide-leave-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.debug-slide-enter-from,
.debug-slide-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(0.97);
}

/* Mobile */
@media (max-width: 600px) {
  .debug-drawer {
    left: 12px;
    right: 12px;
    width: auto;
    max-height: 66vh;
    bottom: 74px;
  }

  .debug-trigger {
    bottom: 16px;
    right: 16px;
  }
}
</style>
